import { ReactNode, useState } from "react"
import { SmModalReact } from "./SmModal"
import { createPortal } from "react-dom"

export interface SmButtonProps {
  /**
   * The ID used to identify this component in Dash callbacks.
   */
  id?: string
  /**
   * Message to show in the confirmation modal. If no message is set, no modal will be shown. Default: None
   */
  confirm_msg?: string
  /**
   * Header to show in the confirmation modal. Default: Please confirm
   */
  confirm_header?: string
  /**
   * If set, this button will act like an anchor and jump to the URL
   */
  confirm_btn?: string
  /**
   * Label for the confirm button. Default: Accept
   */
  link?: string
  /**
   * If set along with link, this will be the 'target' prop in the <a> component (default '_blank')
   */
  link_target?: string
  /**
   * Theme of the button, it can be 'purple', 'black', 'glass', 'purple-outline', 'purple-text' or 'link'
   */
  color?: string
  /**
   * Number of times this component (or the accept button in the confirmation modal) has been clicked
   */
  n_clicks?: number
  /**
   * DO NOT USE, NOT WORKING.
   */
  filled?: boolean
  /**
   * If the button is not enabled, it will not register any clicks. Default: true
   */
  enabled?: boolean
  /**
   * Can be:
   *  - adjust: It will adjust to its content
   *  - auto: It will adjust to its content but will always be at least 100px (default)
   *  - extend: It will occupy all available space
   */
  width?: string
  /**
   * Dict specifying the style of the component
   */
  style?: object
  /**
   * CSS Classes to apply to the component
   */
  className?: string
  /**
   * If true, the button will not show. Default: false
   */
  hidden?: boolean
  /**
   * The children of this component.
   */
  branding?: string
  /**
   * It can be 'old' (default) or 'new'
   */
  children?: ReactNode
  /**
   * Dash-assigned callback that should be called to report property changes
   * to Dash, to make them available for callbacks.
   */
  setProps: (props: Partial<SmButtonProps>) => void
}

/**
 * SmButton is a button that allows to show a confirmation dialog if needed.
 */
const SmButton = ({
  color = "purple",
  filled = true,
  enabled = true,
  hidden = false,
  width = "auto",
  confirm_msg = undefined,
  confirm_header = "Please confirm",
  confirm_btn = "Accept",
  n_clicks = 0,
  link = undefined,
  branding = 'old',
  link_target = "_blank",
  style = {},
  id=undefined,
  className = undefined,
  ...props
}: SmButtonProps) => {
  const { setProps, children } = props
  let _className = `sm-button-${color}`
  if (className) {
    _className = `${_className} ${className}`
  }
  let buttonStyle = {}
  if (width === "auto") {
    buttonStyle = { minWidth: "100px" }
  } else if (width === "extend") {
    buttonStyle = { width: "100%" }
  }

  buttonStyle = { ...buttonStyle, ...style }
  const [isOpen, setIsOpen] = useState(false)

  const confirm = confirm_msg !== undefined && confirm_msg !== null && link === undefined
  let modal = undefined
  let onClick = () => setProps({ n_clicks: n_clicks + 1 })

  function onAccept() {
    setProps({ n_clicks: n_clicks + 1 })
    setIsOpen(false)
  }

  if (confirm) {
    onClick = () => setIsOpen(true)
    modal = (
      <SmModalReact
        is_open={isOpen}
        header={confirm_header}
        setIsOpen={setIsOpen}
        onAccept={onAccept}
        accept={confirm_btn}
      >
        <div className="fs-4 my-4">{confirm_msg}</div>
      </SmModalReact>
    )
  }
  let button = (
    <button id={id} hidden={hidden} disabled={!enabled} className={_className} style={buttonStyle} onClick={onClick}>
      {children}
    </button>
  )
  if (link !== undefined) {
    button = (
      <a href={link} target={link_target}>
        {button}
      </a>
    )
  }
  return (
    <>
      {confirm && createPortal(modal, document.body)}
      {button}
    </>
  )
}

export default SmButton
